import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getUserDetails,
  updateUser,
  deleteUser,
  getUsageAnalytics,
  getRevenueAnalytics,
  getSuccessRateAnalytics,
  getMunicipalitiesStatus,
  verifyMunicipalities,
  refreshStaleMunicipalities,
  getMunicipalityStatistics,
  getVerificationHistory,
  getAdminSearches,
  getAdminSearchDetail,
  exportAdminSearchesCSV,
  type UserListRequest,
  type UserAdminUpdate,
  type AdminSearchRequest,
} from '@/services/admin.service';
import { queryKeys } from '@/lib/queryClient';

/**
 * Admin Hooks
 * React Query hooks for admin-related API operations
 */

// User Management Hooks

/**
 * Hook to get list of users with pagination and filtering
 */
export const useUsers = (params?: UserListRequest) => {
  return useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: () => getUsers(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to get detailed information about a specific user
 */
export const useUserDetails = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.admin.user(userId),
    queryFn: () => getUserDetails(userId),
    enabled: enabled && !!userId && userId > 0,
  });
};

/**
 * Hook to update user account settings
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, updateData }: { userId: number; updateData: UserAdminUpdate }) =>
      updateUser(userId, updateData),
    onSuccess: (_data, variables) => {
      // Invalidate user list and specific user details
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.user(variables.userId) });
    },
  });
};

/**
 * Hook to delete (deactivate) a user account
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => deleteUser(userId),
    onSuccess: () => {
      // Invalidate user list to refresh after deletion
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users() });
    },
  });
};

// Analytics Hooks

/**
 * Hook to get system usage analytics
 */
export const useUsageAnalytics = () => {
  return useQuery({
    queryKey: queryKeys.admin.usageAnalytics,
    queryFn: getUsageAnalytics,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get revenue and financial analytics
 */
export const useRevenueAnalytics = (
  startDate?: string,
  endDate?: string,
  period: 'day' | 'week' | 'month' = 'month'
) => {
  return useQuery({
    queryKey: queryKeys.admin.revenueAnalytics({ startDate, endDate, period }),
    queryFn: () => getRevenueAnalytics(startDate, endDate, period),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get permit extraction success rate analytics
 */
export const useSuccessRateAnalytics = (state?: string, days: number = 30) => {
  return useQuery({
    queryKey: queryKeys.admin.successRateAnalytics({ state, days }),
    queryFn: () => getSuccessRateAnalytics(state, days),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Municipality Management Hooks

/**
 * Hook to get municipality status
 */
export const useMunicipalitiesStatus = (staleOnly: boolean = false) => {
  return useQuery({
    queryKey: queryKeys.admin.municipalities(staleOnly),
    queryFn: () => getMunicipalitiesStatus(staleOnly),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get municipality database statistics
 */
export const useMunicipalityStatistics = () => {
  return useQuery({
    queryKey: queryKeys.admin.municipalityStats,
    queryFn: getMunicipalityStatistics,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to verify municipalities
 */
export const useVerifyMunicipalities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cities, staleOnly }: { cities?: string[]; staleOnly?: boolean }) =>
      verifyMunicipalities(cities, staleOnly),
    onSuccess: () => {
      // Invalidate municipality-related queries after verification
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.municipalities() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.municipalityStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.verificationHistory() });
    },
  });
};

/**
 * Hook to refresh stale municipalities
 */
export const useRefreshStaleMunicipalities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (maxAgeDays: number = 90) => refreshStaleMunicipalities(maxAgeDays),
    onSuccess: () => {
      // Invalidate municipality-related queries after refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.municipalities() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.municipalityStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.verificationHistory() });
    },
  });
};

/**
 * Hook to get verification history
 */
export const useVerificationHistory = (
  municipality?: string,
  status?: string,
  limit: number = 50
) => {
  return useQuery({
    queryKey: queryKeys.admin.verificationHistory({ municipality, status, limit }),
    queryFn: () => getVerificationHistory(municipality, status, limit),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// Search Data Management Hooks

/**
 * Hook to get paginated list of admin searches
 */
export const useAdminSearches = (params?: AdminSearchRequest) => {
  return useQuery({
    queryKey: queryKeys.admin.searchData(params),
    queryFn: () => getAdminSearches(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to get detailed view of a single search
 */
export const useAdminSearchDetail = (searchId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.admin.searchDetail(searchId),
    queryFn: () => getAdminSearchDetail(searchId),
    enabled: enabled && !!searchId,
  });
};

/**
 * Hook to export search data as CSV
 */
export const useExportSearchCSV = () => {
  return useMutation({
    mutationFn: (params?: AdminSearchRequest) => exportAdminSearchesCSV(params),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `search-data-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Failed to export search data:', error);
    },
  });
};
